/**
 * Axios 请求封装（会议室 H5）
 *
 * 职责：
 * 1. 统一 baseURL、超时、凭证
 * 2. 请求头注入 Bearer Token
 * 3. 普通接口：将 https.js 传入的 params 序列化为 JSON 请求体
 * 4. 文件上传：config.data 为 FormData 时不改 body，走 multipart
 * 5. 响应：业务 code === 200 视为成功；401 清登录态并回首页
 *
 * 与 https.js 配合：get/post 使用 config.params；postForm 使用 config.data = FormData
 */

import axios from 'axios'
import { Toast } from 'vant'
import router from '@/router/index'
import { notifyUserInfoUpdated } from '@/tools/utils'


/** 接口根地址 */
const baseURL = 'https://meetingroom.shdspf.cn'


/** 401 登出后 reject 的标记对象，业务 .catch 可用 isUnauthorizedError(err) 忽略 */
const UNAUTHORIZED_REJECT = { __unauthorized: true }

/** 防止多个请求同时 401 时重复清缓存、重复跳转、重复 Toast */
let isHandlingUnauthorized = false

/**
 * 判断业务/协议层 code 是否为未登录或 Token 失效
 * @param {number|string} code 后端 body.code
 */
function isUnauthorizedCode(code) {
    return code === 401 || code === '401'
}

/**
 * 判断是否应走登出逻辑
 * - HTTP 状态 401
 * - 或 HTTP 200 但 body.code 为 401（本项目常见）
 * @param {import('axios').AxiosResponse|undefined} response
 */
function isUnauthorizedResponse(response) {
    if (!response) return false
    if (response.status === 401) return true
    const data = response.data
    if (data && isUnauthorizedCode(data.code)) return true
    return false
}

/**
 * Token 失效 / 未授权：清本地登录信息，通知 Tab 刷新，跳转会议室首页
 * 跳转延迟 150ms，避免与 van-nav-bar 占位高度计算（100ms）冲突导致 DOM 报错
 */
function handleUnauthorized() {
    if (isHandlingUnauthorized) return
    isHandlingUnauthorized = true

    localStorage.removeItem('userInfo')
    localStorage.removeItem('pendingReviewCount')
    notifyUserInfoUpdated()

    Toast('登录已失效，请重新登录')

    const currentPath = router.currentRoute && router.currentRoute.path
    const finish = () => {
        isHandlingUnauthorized = false
    }

    if (currentPath === '/home') {
        finish()
        return
    }

    setTimeout(() => {
        router.replace('/home').finally(finish)
    }, 150)
}

// ---------------------------------------------------------------------------
// Axios 实例
// ---------------------------------------------------------------------------
const service = axios.create({
    baseURL,
    timeout: 8 * 1000,
    withCredentials: true,
})

// ---------------------------------------------------------------------------
// 请求拦截器
// ---------------------------------------------------------------------------
service.interceptors.request.use(
    (config) => {
        const isFormData =
            typeof FormData !== 'undefined' && config.data instanceof FormData

        // 非 FormData：body 为 JSON 字符串（来源于 config.params，见 https.js）
        if (!isFormData) {
            config.data = JSON.stringify(config.params)
        }

        let userInfo = null
        try {
            userInfo = JSON.parse(localStorage.getItem('userInfo'))
        } catch (e) {
            userInfo = null
        }

        const headers = {
            ...(config.headers || {}),
            Accept: 'application/json',
        }
        if (isFormData) {
            delete headers['Content-Type']
        } else {
            headers['Content-Type'] = 'application/json;charset=UTF-8'
        }
        if (userInfo && userInfo.token) {
            headers.Authorization = `Bearer ${userInfo.token}`
        }
        config.headers = headers

        console.log('请求地址URL------------->' + config.baseURL + config.url)
        console.log('\n请求头------------->' + JSON.stringify(config.headers))
        if (isFormData) {
            console.log('\nbody-------------> FormData(multipart)')
        } else {
            console.log('\n请求参数params------------->', config.data)
        }
        return config
    },
    (error) => Promise.reject(error)
)

// ---------------------------------------------------------------------------
// 响应拦截器
// ---------------------------------------------------------------------------
service.interceptors.response.use(
    (response) => {
        // 业务 body 内 code=401（HTTP 仍为 200 的情况）
        if (isUnauthorizedResponse(response)) {
            handleUnauthorized()
            return Promise.reject(UNAUTHORIZED_REJECT)
        }

        // 约定：code == 200 为成功，直接返回整包 data（含 data、message 等字段）
        if (response.data.code == 200) {
            console.log('success----->', response.data)
            return response.data
        }

        console.log('error----->', response.data)
        Toast(response.data.msg || response.data.message || '请求失败')
        return Promise.reject(response.data)
    },
    (error) => {
        // HTTP 层 401 等
        if (isUnauthorizedResponse(error.response)) {
            handleUnauthorized()
            return Promise.reject(UNAUTHORIZED_REJECT)
        }

        Toast('请求数据失败')

        if (error && error.response) {
            switch (error.response.status) {
                case 400:
                    console.log('------->错误请求')
                    break
                case 401:
                    console.log('------->未授权，请重新登录')
                    break
                case 403:
                    error.message = '拒绝访问'
                    break
                case 404:
                    error.message = '请求错误,未找到该资源'
                    window.location.href = '/'
                    break
                case 405:
                    error.message = '请求方法未允许'
                    break
                case 408:
                    error.message = '请求超时'
                    break
                case 500:
                    error.message = '服务器端出错'
                    break
                case 501:
                    error.message = '网络未实现'
                    break
                case 502:
                    error.message = '网络错误'
                    break
                case 503:
                    error.message = '服务不可用'
                    break
                case 504:
                    error.message = '网络超时'
                    break
                case 505:
                    error.message = 'http版本不支持该请求'
                    break
                default:
                    error.message = `连接错误${error.response.status}`
            }
        } else if (JSON.stringify(error).includes('timeout')) {
            // 请求超时（无 response）
        }

        return Promise.reject(error.response)
    }
)

export default service
